/* eslint-disable indent */
import { camelCase, find, first, get, set } from 'lodash';
import { getIn } from 'formik';
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import diff from 'deep-diff';
import { getCityWithAreasLabel, getOption, getPageParamFromQuery } from '../../utils/common';
import {
  MEDIA_ATTRIBUTES_TYPES_IDS,
  MEDIA_TAGS_TYPES_IDS,
  MEDIA_TYPES_IDS,
  PERFORMANCE_TYPES_ID,
  PRIVACY_ATTRIBUTE_IDS,
  PRODUCTION_ADDED_IDENTIFIERS_IDS,
  ROLE,
  SINGER_PROFESSION_TYPE_ID,
  TAGS_AVAILABILITY_TYPES,
  TP,
  VIDEO_ATTRIBUTE_TYPES_IDS,
} from '../../constants/consts';
import { cardFields, EDITING_PRODUCTION_ENTITY_TYPES, publishedCardFields } from './constants';
import { performanceStatuses } from '../../components/PerformanceCardHeader/PerformanceDate';
import { artistsPaths, companiesPaths, festivalPaths } from '../../constants/routes';
import { getCompanyRoute } from '../Company/utils';
import { getArtistRoute } from '../Artist/utils';
import { getFestivalRoute } from '../Festival/utils';
import { createEntity } from '../App/actions';
import { PRODUCTIONS } from '../../constants/endpointConstants';
import { createProductionAction } from './actions';

export const getCastStepValuesForUpdate = (castFormValues, castType) =>
  castFormValues.reduce(
    (acc, castProfession) => [
      ...acc,
      ...castProfession?.artists
        .filter(a => a?.value)
        .map(artist =>
          // id === cast id, value === profile id
          artist?.id && !castProfession?.isRef
            ? { id: artist?.id }
            : {
                ...(artist?.id ? { id: artist?.id } : {}),
                profile: { id: artist?.value },
                ...(castType === ROLE
                  ? {
                      profession: { id: SINGER_PROFESSION_TYPE_ID },
                      workRole: castProfession?.isRef ? { name: castProfession?.name } : { id: castProfession?.id },
                      professionQualification:
                        castProfession?.isRef || !castProfession?.professionQualification?.id
                          ? null
                          : { id: castProfession?.professionQualification?.id },
                    }
                  : {
                      profession: castProfession?.isRef ? { name: castProfession?.name } : { id: castProfession?.id },
                    }),
              },
        ),
    ],
    [],
  );

export const getValueOrRef = (castItem, field, reference) => {
  if (get(castItem, `${field}.id`)) {
    return get(castItem, field);
  }
  if (get(castItem, reference)) {
    return {
      // to prevent [], . and other symbols in id
      id: `ref_${camelCase(get(castItem, reference, ''))}`,
      name: get(castItem, reference, ''),
      isRef: true,
    };
  }
  return null;
};

export const getPerformanceCastCrewOptions = cast => {
  const professionsOptions = {};
  const rolesOptions = {};
  cast?.forEach(castItem => {
    const workRole = getValueOrRef(castItem, 'workRole', 'workRoleReference');
    const profession = getValueOrRef(castItem, 'profession', 'professionReference');
    if (workRole?.id) {
      const roleId = workRole?.id;
      set(rolesOptions, roleId, [
        ...get(rolesOptions, roleId, []),
        getOption(castItem, 'profile.id', 'profile.name', {}, true),
      ]);
    } else if (profession?.id) {
      const professionId = profession?.id;
      set(professionsOptions, professionId, [
        ...get(professionsOptions, professionId, []),
        getOption(castItem, 'profile.id', 'profile.name', {}, true),
      ]);
    }
  });
  return { professionsOptions, rolesOptions };
};

export const seaparateCastToRolesProfessions = casts => {
  const crew = [];
  const cast = [];
  const other = [];
  (casts || []).forEach(castItem => {
    const workRole = getValueOrRef(castItem, 'workRole', 'workRoleReference');
    const profession = getValueOrRef(castItem, 'profession', 'professionReference');
    if (workRole?.id) {
      if (workRole?.isRef) {
        other.push(castItem);
      } else {
        cast.push(castItem);
      }
    } else if (profession?.id) {
      crew.push(castItem);
    }
  });
  return { crew, cast, other };
};

export const getPerformancesValuesForUpdate = ({ cast, crew }) => {
  const casts = [];
  Object.keys(crew).forEach(key => {
    const roleCastMembers = get(crew, key, []);
    roleCastMembers.forEach(castMember => {
      if (castMember?.id) {
        casts.push({
          id: castMember?.id,
        });
      }
    });
  });
  Object.keys(cast).forEach(key => {
    const roleCastMembers = get(cast, key, []);
    roleCastMembers.forEach(castMember => {
      if (castMember?.id) {
        casts.push({
          id: castMember?.id,
        });
      }
    });
  });
  return casts;
};

export const getOrchestraChoirCast = (cast, professionId) => ({
  ...(cast.id ? { id: cast.id } : {}),
  profession: { id: professionId },
  profile: { name: cast?.label },
});

export const getRolesWithUpdatedName = roles =>
  roles.map(role => ({
    ...role,
    name: role?.name,
    initName: role?.name,
    originalName: role?.original_name,
    qualificationName: role?.professionQualification?.name,
  }));

export const getPerformancesFormValues = (production, date, perf) => {
  const isCanceled = Boolean(find(perf?.performanceTypes, { id: PERFORMANCE_TYPES_ID.CANCELLED }));
  const commonPerformance = {
    date,
    isPublished: Boolean(production?.published_at),
    status: isCanceled ? performanceStatuses.cancelled : performanceStatuses.regular,
    casts: null,
    crew: null,
    [cardFields.country.name]: getOption(perf?.country || perf?.venue?.country),
    [cardFields.city.name]: getOption(perf?.city, 'id', getCityWithAreasLabel),
    [cardFields.venue.name]: getOption(perf?.venue),
    [cardFields.stage.name]: getOption(perf?.venueStage),
    [cardFields.time.name]: [],
    [cardFields.isTour.name]: Boolean(find(perf?.performanceTypes, { id: PERFORMANCE_TYPES_ID.TOUR })),
    [publishedCardFields.cancel.name]: isCanceled,
    [cardFields.isFestivalPart.name]:
      production?.festivals?.length > 0 &&
      Boolean(
        find(perf?.performanceTypes, {
          id: PERFORMANCE_TYPES_ID.FESTIVAL,
        }),
      ),
    [cardFields.festival.name]: getOption(perf?.festival?.id ? perf?.festival : first(production?.festivals)),
  };
  return commonPerformance;
};

export const getMediaBodyForModifying = ({
  specificAttributes,
  recordToEdit,
  recordFormValues,
  productionId,
  recordTypeSlug,
  formFields,
}) => {
  const isExist = !!recordFormValues?.id;
  const performanceDate = getIn(recordFormValues, formFields.performanceDate.name);
  const costType = getIn(recordFormValues, `${formFields.costType.name}.value`);
  const thumbnail = getIn(recordFormValues, formFields.thumbnail.name);
  const tags = recordToEdit?.tags?.filter(tag => tag?.tagType?.id !== MEDIA_TAGS_TYPES_IDS.DATE) || [];
  const notConnectedAttributes = (recordToEdit?.attributes || []).filter(
    attr =>
      ![...specificAttributes?.map(specificAttr => specificAttr?.typeId), VIDEO_ATTRIBUTE_TYPES_IDS.COST_TYPE].includes(
        attr?.attributeType?.id,
      ),
  );
  const data = {
    url: get(recordFormValues, formFields.url.name),
    attributes: notConnectedAttributes || [],
    thumbnail: thumbnail ? { data: `@${thumbnail?.filename}` } : null,
    tags,
  };

  if (recordFormValues?.updateThumbnail) {
    data.thumbnail = recordFormValues?.thumbnail;
  }

  if (costType) {
    data.attributes.push({
      attributeType: { id: VIDEO_ATTRIBUTE_TYPES_IDS.COST_TYPE },
      id: getIn(recordFormValues, `${formFields.costType.name}.value`),
    });
  }
  if (performanceDate && getIn(recordFormValues, formFields.isLiveEventConnected.name)) {
    data.tags.push({
      tagType: { id: MEDIA_TAGS_TYPES_IDS.DATE },
      tagId: performanceDate?.id,
      availabilityTypes: [TAGS_AVAILABILITY_TYPES.GALLERY],
    });
  }

  specificAttributes.forEach(field => {
    const value = getIn(recordFormValues, field?.name);
    const name = value?.value || value;
    if (name) {
      data.attributes.push({ attributeType: { id: field?.typeId }, name });
    }
  });

  if (!isExist) {
    Object.assign(data, {
      mediaType: {
        id: MEDIA_TYPES_IDS.VIDEO,
      },
      attributes: [
        ...data?.attributes,
        { attributeType: { id: MEDIA_ATTRIBUTES_TYPES_IDS.VIDEO }, slug: recordTypeSlug },
      ],
      tags: [
        {
          tagType: { id: MEDIA_TAGS_TYPES_IDS.PRODUCTION },
          tagId: productionId,
          availabilityTypes: [TAGS_AVAILABILITY_TYPES.GALLERY],
          privacyAttributes: [
            {
              id: PRIVACY_ATTRIBUTE_IDS.PUBLIC,
            },
          ],
        },
        ...data?.tags,
      ],
    });
  }
  return data;
};

export const getPathForCompanyEditStep = ({ entity, production, step }) => ({
  asPath: companiesPaths.addProduction,
  path: getPageParamFromQuery(`${getCompanyRoute(entity)}/edit/modify-production`, {
    production_id: production?.id,
    step,
  }),
});
export const getPathForArtistsEditStep = ({ entity, production, step }) => ({
  asPath: artistsPaths.modifyProduction,
  path: getPageParamFromQuery(`${getArtistRoute(entity)}/edit/modify-production`, {
    production_id: production?.id,
    step,
  }),
});
export const getPathForFestivalEditStep = ({ entity, production, step }) => ({
  asPath: festivalPaths.modifyProduction,
  path: getPageParamFromQuery(`${getFestivalRoute(entity)}/edit/modify-production`, {
    production_id: production?.id,
    step,
  }),
});

export const getQueryFuncForEditStep = type => {
  switch (type) {
    case EDITING_PRODUCTION_ENTITY_TYPES.COMPANY:
      return getPathForCompanyEditStep;
    case EDITING_PRODUCTION_ENTITY_TYPES.FESTIVAL:
      return getPathForFestivalEditStep;
    case EDITING_PRODUCTION_ENTITY_TYPES.ARTIST:
      return getPathForArtistsEditStep;
    default:
      return () => {};
  }
};

const checkForRolesNamesDuplicates = (values, val, index) =>
  values?.slice(0, index).find(el => (el?.isRef ? el?.name : el?.originalName)?.toLowerCase() === val?.toLowerCase());

const checkForProfessionsNamesDuplicates = (values, val, index) =>
  values?.slice(0, index).find(el => el?.name?.toLowerCase() === val?.toLowerCase());

const validateRoleName = (rolesValues, name, index) => {
  let error;
  const trimedName = name?.trim();
  if (name?.length > 0 && checkForRolesNamesDuplicates(rolesValues, trimedName, index)) {
    error = `${TP}.FN_DUPLICATED_VALUE`;
  }
  return error;
};

const validateProfessionName = (professionValues, name, index) => {
  let error;
  const trimedName = name?.trim();
  if (trimedName?.length > 0 && checkForProfessionsNamesDuplicates(professionValues, trimedName, index)) {
    error = `${TP}.FN_DUPLICATED_VALUE`;
  } else if (trimedName?.length === 0) {
    error = `${TP}.FN_REQUIRED_FIELD`;
  }
  return error;
};

const validateArtistPerRole = (artistsValues, option, index) => {
  let error;
  if (artistsValues?.slice(0, index)?.find(el => el?.value === option?.value)) {
    error = `${TP}.FN_DUPLICATED_VALUE`;
  }
  return error;
};

const validateRoleArtistPerRole = (artistsValues, option, index, isRef) => {
  let error = validateArtistPerRole(artistsValues, option, index);
  if (index === artistsValues?.length - 1 && isRef && !option?.value) {
    error = `${TP}.FN_REQUIRED_FIELD`;
  }
  return error;
};

const rolesValidation = (typeName, values, nameValidation, errors) => {
  values.forEach((role, index) => {
    const fieldName = `${typeName}[${index}]`;
    const nameError = role?.isRef && nameValidation(values, role?.name, index);
    if (nameError) {
      set(errors, `${fieldName}.name`, nameError);
    }
    const artists = role?.artists || [];
    artists.forEach((artist, artistIndex) => {
      const artistError = validateRoleArtistPerRole(artists, artist, artistIndex, role?.isRef);
      if (artistError) {
        set(errors, `${fieldName}.artists[${artistIndex}]`, artistError);
      }
    });
  });
};

const validateChorusOrchestras = (typeName, values, errors) => {
  values.forEach((artist, index) => {
    const fieldName = `${typeName}[${index}]`;
    const artistError = validateArtistPerRole(values, artist, index);
    if (artistError) {
      set(errors, fieldName, artistError);
    }
  });
};

export const validateCastForm = values => {
  const errors = {};
  const { choirs, orchestras, roles, professions } = values;
  rolesValidation('roles', roles, validateRoleName, errors);
  rolesValidation('professions', professions, validateProfessionName, errors);
  validateChorusOrchestras('choirs', choirs, errors);
  validateChorusOrchestras('orchestras', orchestras, errors);
  return errors;
};

export function getEntityIdentifierByProductionType(entityType) {
  switch (entityType) {
    case EDITING_PRODUCTION_ENTITY_TYPES.COMPANY:
      return PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY;
    case EDITING_PRODUCTION_ENTITY_TYPES.FESTIVAL:
      return PRODUCTION_ADDED_IDENTIFIERS_IDS.FESTIVAL;
    case EDITING_PRODUCTION_ENTITY_TYPES.ARTIST:
      return PRODUCTION_ADDED_IDENTIFIERS_IDS.ARTIST;
    default:
      return PRODUCTION_ADDED_IDENTIFIERS_IDS.UNKNOWN;
  }
}

function useCreateProductionForEntity(entityType) {
  const dispatch = useDispatch();
  const productionIdentifier = getEntityIdentifierByProductionType(entityType);
  return useCallback(
    (entityId, callback) => {
      dispatch(
        createEntity({
          endpoint: PRODUCTIONS,
          sagaRoutine: createProductionAction,
          params: {
            addedBy: { id: productionIdentifier },
            forEntity: productionIdentifier,
            forEntityId: entityId,
            isNewProduction: false,
          },
          callback,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productionIdentifier],
  );
}

export function useCreateProductionForCompanyEntity() {
  return useCreateProductionForEntity(EDITING_PRODUCTION_ENTITY_TYPES.COMPANY);
}

export function useCreateProductionForFestivalEntity() {
  return useCreateProductionForEntity(EDITING_PRODUCTION_ENTITY_TYPES.FESTIVAL);
}

export function useCreateProductionForArtistEntity() {
  return useCreateProductionForEntity(EDITING_PRODUCTION_ENTITY_TYPES.ARTIST);
}

export function getChangesAddedEntityList(changes) {
  const enabledList = [];

  if (changes?.length < 1) {
    return [];
  }

  const addedList = changes?.filter(change => change?.kind === 'A' && change?.item?.kind === 'N') ?? [];

  if (addedList?.length < 1) {
    return [];
  }

  addedList.forEach(change => {
    diff.applyChange(enabledList, true, change);
  });

  return Object.values(enabledList)
    ?.filter(change => change?.id)
    ?.map(change => change.id);
}

function allArrayElementsInAnother(array1, array2) {
  if (!Array.isArray(array1) || !Array.isArray(array2)) {
    return false;
  }
  return array1.every(item => array2.includes(item));
}

export function isAllDatesSelected(allDates, castDates) {
  if (!Array.isArray(allDates) || !Array.isArray(castDates)) {
    return false;
  }
  const dateIds = allDates.map(date => date?.id);
  const castDateIds = castDates.map(date => date?.dateId);
  return allArrayElementsInAnother(dateIds, castDateIds);
}
